import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Lock, RefreshCw, ArrowLeft, Mail, Music, FileText } from "lucide-react";

import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "onesound_admin_token";

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const adminHeaders = () => ({ "X-Admin-Token": getAdminToken() });

export const AdminAuth = ({ children, onAuthed }) => {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = getAdminToken();
    if (!t) {
      setChecking(false);
      return;
    }
    // Validate by hitting a guarded endpoint
    axios
      .get(`${API}/quotes`, { headers: { "X-Admin-Token": t } })
      .then(() => {
        setAuthed(true);
        onAuthed?.();
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setChecking(false));
  }, [onAuthed]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { token: pwd });
      localStorage.setItem(TOKEN_KEY, pwd);
      setAuthed(true);
      onAuthed?.();
      toast.success("Signed in.");
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-12 w-12 rounded-full border-2 border-white/10 border-t-brand-magenta animate-spin" />
      </div>
    );
  }

  if (authed) return children;

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-6 bg-background"
    >
      <form
        onSubmit={onLogin}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.02] p-8 lg:p-10 backdrop-blur-md"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-12" />
          <div>
            <p className="font-display font-black text-base text-brand-gradient">
              ONESOUND
            </p>
            <p className="font-body text-[10px] uppercase tracking-[0.25em] text-white/60">
              Admin
            </p>
          </div>
        </div>
        <h1 className="font-display font-black text-3xl text-white tracking-tight">
          Sign in to dashboard
        </h1>
        <p className="mt-3 font-body text-sm text-white/60">
          Enter the admin token to manage quotes and portfolio.
        </p>

        <div className="mt-8 space-y-2">
          <Label
            htmlFor="admin-token"
            className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
          >
            Admin Token
          </Label>
          <div className="relative">
            <Lock
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
            />
            <Input
              id="admin-token"
              data-testid="admin-token-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••••••"
              className="bg-black/40 border-white/10 text-white pl-11 h-12 rounded-xl focus-visible:ring-brand-magenta"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full h-12 rounded-full bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
        >
          {loading ? "Signing in..." : "Sign in"}
        </Button>

        <Link
          to="/"
          data-testid="admin-back-home"
          className="mt-6 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white"
        >
          <ArrowLeft size={14} />
          Back to site
        </Link>
      </form>
    </div>
  );
};

export const AdminShell = ({ children, onRefresh, refreshing }) => {
  const loc = useLocation();
  const navigate = useNavigate();

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/quotes");
    window.location.reload();
  };

  const navItem = (to, label, Icon) => {
    const active = loc.pathname.startsWith(to);
    return (
      <Link
        key={to}
        to={to}
        data-testid={`admin-nav-${label.toLowerCase()}`}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-xs uppercase tracking-[0.18em] border transition-colors ${
          active
            ? "bg-white text-black border-white"
            : "bg-white/[0.02] text-white/65 border-white/10 hover:text-white hover:border-white/30"
        }`}
      >
        <Icon size={14} /> {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-10" />
            <div className="hidden sm:block">
              <p className="font-display font-black text-sm text-brand-gradient">
                ONESOUND
              </p>
              <p className="font-body text-[9px] uppercase tracking-[0.25em] text-white/60">
                Admin Console
              </p>
            </div>
          </Link>
          <nav className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {navItem("/admin/quotes", "Quotes", Mail)}
            {navItem("/admin/tracks", "Tracks", Music)}
            {navItem("/admin/contracts", "Contracts", FileText)}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            {onRefresh && (
              <Button
                type="button"
                variant="outline"
                onClick={onRefresh}
                data-testid="admin-refresh"
                disabled={refreshing}
                className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                <RefreshCw
                  size={14}
                  className={refreshing ? "animate-spin" : ""}
                />
              </Button>
            )}
            <Button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="rounded-full bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
            >
              <LogOut size={14} className="mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        {children}
      </main>
    </div>
  );
};
